import { Psd, Layer } from 'ag-psd';
import { LayerJSON, PSDJSON, CanvasJSON } from './types';
import { getApiUrl } from '../config/api';
import { hashBuffer, getCachedImageUrl, saveImageUrlToCache, saveS3CacheToStorage, loadS3CacheFromStorage } from './cacheManager';

let layerIdCounter = 0;

function generateLayerId(): string {
  return `layer_${layerIdCounter++}`;
}

function getBlendMode(layer: Layer): string {
  return layer.blendMode || 'normal';
}

function getBoundingBox(layer: Layer): { x: number; y: number; width: number; height: number } {
  const left = layer.left || 0;
  const top = layer.top || 0;
  const right = layer.right || 0;
  const bottom = layer.bottom || 0;
  
  return {
    x: left,
    y: top,
    width: right - left,
    height: bottom - top
  };
}

function isTextLayer(layer: Layer): boolean {
  return 'text' in layer && layer.text !== undefined;
}

function isImageLayer(layer: Layer): boolean {
  return 'canvas' in layer && layer.canvas !== undefined;
}

function isGroupLayer(layer: Layer): boolean {
  return 'children' in layer && Array.isArray(layer.children);
}

// Interface for image data to be uploaded
interface ImageToUpload {
  layerId: string;
  buffer: Uint8Array;
  filename: string;
  hash?: string;
}

// Initialize cache from cacheManager
loadS3CacheFromStorage();

// Extract image buffer (browser-compatible)
async function extractImageBuffer(layer: Layer, layerId: string): Promise<Uint8Array | undefined> {
  if (!layer.canvas) {
    return undefined;
  }

  // Convert canvas to blob/data URL (browser-compatible)
  return new Promise((resolve, reject) => {
    try {
      if (typeof (layer.canvas as any).toBlob === 'function') {
        (layer.canvas as any).toBlob(async (blob: Blob | null) => {
          try {
            if (blob) {
              const arrayBuffer = await blob.arrayBuffer();
              resolve(new Uint8Array(arrayBuffer));
            } else {
              resolve(undefined);
            }
          } catch (error) {
            console.warn(`Warning: Could not extract image for layer ${layerId}:`, error);
            resolve(undefined);
          }
        }, 'image/png');
      } else if (typeof (layer.canvas as any).toDataURL === 'function') {
        const dataURL = (layer.canvas as any).toDataURL('image/png');
        const base64 = dataURL.split(',')[1];
        const binaryString = atob(base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          const codePoint = binaryString.codePointAt(i);
          if (codePoint !== undefined) {
            bytes[i] = codePoint;
          }
        }
        resolve(bytes);
      } else {
        console.warn(`Warning: Canvas does not support toBlob or toDataURL for layer ${layerId}`);
        resolve(undefined);
      }
    } catch (error) {
      console.warn(`Warning: Could not extract image for layer ${layerId}:`, error);
      resolve(undefined);
    }
  });
}

// S3 upload helper functions (browser-compatible using fetch)
const SIGN_API_URL = getApiUrl('/api/templates/v1.0.0/upload/sign');

interface SignResponse {
  uploads: Array<{
    key: string;
    uploadUrl: string;
    finalUrl: string;
  }>;
}

async function getSignedUploadUrls(imageFiles: Array<{ filename: string; mime: string }>): Promise<SignResponse> {
  const requestBody = {
    assets: imageFiles.map(f => ({ filename: f.filename, mime: f.mime }))
  };
  
  const response = await fetch(SIGN_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(requestBody)
  });

  if (!response.ok) {
    throw new Error(`Failed to get signed URLs: ${response.statusText}`);
  }

  return await response.json();
}

async function uploadToS3(uploadUrl: string, fileBuffer: Uint8Array, contentType: string): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': contentType,
    },
    body: fileBuffer
  });

  if (!response.ok) {
    throw new Error(`S3 upload failed: ${response.status} - ${response.statusText}`);
  }
}

// Helper: Check cache and separate images into cached and uncached
async function processImageCache(
  images: ImageToUpload[],
  layerIdToUrlMap: Map<string, string>
): Promise<ImageToUpload[]> {
  const imagesToUpload: ImageToUpload[] = [];
  
  for (const image of images) {
    const hash = await hashBuffer(image.buffer);
    image.hash = hash;
    
    const cachedUrl = getCachedImageUrl(hash);
    if (cachedUrl) {
      console.log(`✅ Cache hit for ${image.filename} (hash: ${hash.substring(0, 8)}...)`);
      layerIdToUrlMap.set(image.layerId, cachedUrl);
    } else {
      imagesToUpload.push(image);
    }
  }
  
  return imagesToUpload;
}

// Helper: Upload a single image to S3
async function uploadSingleImage(
  image: ImageToUpload,
  uploadInfo: { uploadUrl: string; finalUrl: string },
  index: number,
  total: number,
  layerIdToUrlMap: Map<string, string>
): Promise<void> {
  try {
        await uploadToS3(uploadInfo.uploadUrl, image.buffer, 'image/png');
        console.log(`✅ Uploaded [${index + 1}/${total}]: ${uploadInfo.finalUrl}`);
        
        if (image.hash) {
          saveImageUrlToCache(image.hash, uploadInfo.finalUrl);
        }
        
        layerIdToUrlMap.set(image.layerId, uploadInfo.finalUrl);
    
    if (index < total - 1) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error(`❌ Failed to upload ${image.filename}:`, errorMessage);
  }
}

// Upload images to S3 and return mapping of layerId to S3 URL
export async function uploadImagesToS3(images: ImageToUpload[]): Promise<Map<string, string>> {
  const layerIdToUrlMap = new Map<string, string>();

  if (images.length === 0) {
    return layerIdToUrlMap;
  }

  try {
    console.log(`Processing ${images.length} images (checking cache)...`);
    
    const imagesToUpload = await processImageCache(images, layerIdToUrlMap);
    const cacheHits = images.length - imagesToUpload.length;
    
    if (cacheHits > 0) {
      console.log(`📦 Cache hits: ${cacheHits}/${images.length} images`);
    }

    if (imagesToUpload.length === 0) {
      console.log(`✅ All ${images.length} images found in cache, skipping upload`);
      return layerIdToUrlMap;
    }

    const imageFiles = imagesToUpload.map((img) => ({
      filename: img.filename,
      mime: 'image/png'
    }));

    console.log(`Requesting signed URLs for ${imageFiles.length} new images...`);
    const signResponse = await getSignedUploadUrls(imageFiles);

    if (signResponse.uploads?.length !== imagesToUpload.length) {
      throw new Error(`Mismatch: requested ${imagesToUpload.length} URLs, got ${signResponse.uploads?.length || 0}`);
    }

    console.log(`Uploading ${imagesToUpload.length} new images to S3...`);
    for (let i = 0; i < imagesToUpload.length; i++) {
      await uploadSingleImage(imagesToUpload[i], signResponse.uploads[i], i, imagesToUpload.length, layerIdToUrlMap);
    }

    if (imagesToUpload.length > 0) {
      saveS3CacheToStorage();
    }

    const totalUploaded = layerIdToUrlMap.size;
    console.log(`✅ Successfully processed ${totalUploaded} images (${cacheHits} from cache, ${imagesToUpload.length} uploaded)`);
    return layerIdToUrlMap;
  } catch (error) {
    console.error('Error uploading images to S3:', error);
    throw error;
  }
}

const WEIGHT_SUFFIXES = [
  'extrabolditalic', 'ultrabolditalic', 'semibolditalic', 'demibolditalic', 'extralightitalic', 'ultralightitalic',
  'extrabold', 'ultrabold', 'semibold', 'demibold', 'extralight', 'ultralight',
  'regularitalic', 'bolditalic', 'lightitalic', 'mediumitalic', 'thinitalic', 'blackitalic', 'heavyitalic',
  'regular', 'bold', 'light', 'medium', 'thin', 'black', 'heavy',
] as const;

function stripWeightSuffix(s: string): string {
  const lower = s.toLowerCase();
  for (const suf of WEIGHT_SUFFIXES) {
    if (lower.endsWith(suf)) {
      let trimmed = s.slice(0, -suf.length);
      while (trimmed.length > 0 && (trimmed.endsWith('-') || trimmed.endsWith('_'))) {
        trimmed = trimmed.slice(0, -1);
      }
      return trimmed;
    }
  }
  if (lower.endsWith('italic')) {
    return s.slice(0, -5);
  }
  return s;
}

// Normalize font family name (no regex - avoids ReDoS)
function normalizeFontFamily(fontName: string): string {
  let normalized = stripWeightSuffix(stripWeightSuffix(fontName));
  let prev = '';
  while (prev !== normalized && (normalized.endsWith('-') || normalized.endsWith('_'))) {
    prev = normalized;
    normalized = normalized.slice(0, -1);
  }
  normalized = normalized.toLowerCase().trim();
  return normalized || fontName.toLowerCase();
}

function isWeightSuffix(s: string): boolean {
  const lower = s.toLowerCase();
  for (const suf of WEIGHT_SUFFIXES) {
    if (lower === suf) return true;
  }
  return false;
}

function extractWeightSuffix(fontName: string): string {
  const dashParts = fontName.split('-');
  if (dashParts.length >= 2) {
    const suffix = dashParts.slice(1).join('-').toLowerCase();
    if (isWeightSuffix(suffix)) return suffix;
  }
  const lower = fontName.toLowerCase();
  for (const suf of WEIGHT_SUFFIXES) {
    if (lower.endsWith(suf)) return suf;
  }
  return '';
}

function mapWeightSuffixToNumber(suffix: string): number {
  const suffixLower = suffix.replace('italic', '').trim();
  
  if (suffixLower.includes('thin')) {
    return 100;
  } else if (suffixLower.includes('extralight') || suffixLower.includes('ultralight')) {
    return 200;
  } else if (suffixLower.includes('light')) {
    return 300;
  } else if (suffixLower.includes('regular') || suffixLower === '') {
    return 400;
  } else if (suffixLower.includes('medium')) {
    return 500;
  } else if (suffixLower.includes('semibold') || suffixLower.includes('demibold')) {
    return 600;
  } else if (suffixLower.includes('bold')) {
    return 700;
  } else if (suffixLower.includes('extrabold') || suffixLower.includes('ultrabold')) {
    return 800;
  } else if (suffixLower.includes('black') || suffixLower.includes('heavy')) {
    return 900;
  }
  
  return 400;
}

function parseFigmaLikeFont(fontName: string): {
  fontFamily: string;
  fontPostScriptName: string;
  fontWeight: number;
  fontStyle: 'normal' | 'italic';
} {
  const fontPostScriptName = fontName;
  const normalizedFamily = normalizeFontFamily(fontName);
  const fontStyle: 'normal' | 'italic' = 
    fontName.toLowerCase().includes('italic') ? 'italic' : 'normal';
  
  let fontWeight: number;
  
  if (fontName.includes('-')) {
    const weightSuffix = extractWeightSuffix(fontName);
    if (weightSuffix.length > 0) {
      fontWeight = mapWeightSuffixToNumber(weightSuffix);
    } else {
      fontWeight = 400;
    }
  } else {
    fontWeight = 400;
  }

  return {
    fontFamily: normalizedFamily,
    fontPostScriptName,
    fontWeight,
    fontStyle
  };
}

// Helper: Extract font info from style
function extractFontFromStyle(style: any, textData: Partial<LayerJSON>): void {
  const fontName: string | undefined =
    style.font?.name || style.fontName || style.font?.postScriptName;
  if (fontName) {
    const figmaFont = parseFigmaLikeFont(fontName);
    textData.fontFamily = figmaFont.fontFamily;
    textData.fontPostScriptName = figmaFont.fontPostScriptName;
    textData.fontWeight = figmaFont.fontWeight;
    textData.fontStyle = figmaFont.fontStyle;
  }
}

// Helper: Convert color value to 0-255 range
function normalizeColorValue(value: number): number {
  return value <= 1 ? Math.round(value * 255) : Math.round(value);
}

// Helper: Extract color from object format
function extractColorFromObject(fillColor: { r?: number; g?: number; b?: number; a?: number }, textData: Partial<LayerJSON>): void {
  const r = typeof fillColor.r === 'number' ? fillColor.r : undefined;
  const g = typeof fillColor.g === 'number' ? fillColor.g : undefined;
  const b = typeof fillColor.b === 'number' ? fillColor.b : undefined;
  
  if (typeof fillColor.a === 'number') {
    textData.fillColorAlpha = fillColor.a;
  }
  
  if (r !== undefined && g !== undefined && b !== undefined) {
    textData.color = `rgb(${normalizeColorValue(r)}, ${normalizeColorValue(g)}, ${normalizeColorValue(b)})`;
  }
}

// Helper: Extract color from array format
function extractColorFromArray(fillColor: number[], textData: Partial<LayerJSON>): void {
  const rr = normalizeColorValue(fillColor[0]);
  const gg = normalizeColorValue(fillColor[1]);
  const bb = normalizeColorValue(fillColor[2]);
  
  if (fillColor.length >= 4 && typeof fillColor[3] === 'number') {
    textData.fillColorAlpha = fillColor[3];
  }
  
  textData.color = `rgb(${rr}, ${gg}, ${bb})`;
}

// Helper: Extract color from fillColor
function extractColorFromFill(fillColor: any, textData: Partial<LayerJSON>): void {
  if (typeof fillColor === 'object' && fillColor !== null) {
    if (Array.isArray(fillColor) && fillColor.length >= 3) {
      extractColorFromArray(fillColor, textData);
    } else {
      extractColorFromObject(fillColor, textData);
    }
  }
}

// Helper: Extract font from engineData
function extractFontFromEngineData(engineData: any, textData: Partial<LayerJSON>): void {
  if (engineData?.Editor?.Text?.Fonts) {
    const fonts = engineData.Editor.Text.Fonts;
    if (fonts && fonts.length > 0) {
      const font = fonts[0];
      
      if (!textData.fontFamily) {
        const fontName =
          font.Name ||
          font.name ||
          font.FontName ||
          font.fontName;
        
        if (fontName) {
          const figmaFont = parseFigmaLikeFont(fontName);
          textData.fontFamily = figmaFont.fontFamily;
          textData.fontPostScriptName = figmaFont.fontPostScriptName;
          textData.fontWeight = figmaFont.fontWeight;
          textData.fontStyle = figmaFont.fontStyle;
        }
      }
      
      if (!textData.fontSize) {
        textData.fontSize =
          font.Sizes?.[0] ||
          font.sizes?.[0] ||
          font.Size ||
          font.size;
      }
    }
  }
}

// Helper: Extract alignment from engineData
function extractAlignmentFromEngineData(engineData: any, textData: Partial<LayerJSON>): void {
  if (engineData?.Editor?.Text?.Paragraphs) {
    const paragraphs = engineData.Editor.Text.Paragraphs;
    if (paragraphs && paragraphs.length > 0) {
      const paragraph = paragraphs[0];
      const align = paragraph.Justification || paragraph.justification || paragraph.Alignment || paragraph.alignment;
      if (!textData.textAlign) {
        if (align === 0 || align === 'left' || align === 'LEFT') {
          textData.textAlign = 'left';
        } else if (align === 1 || align === 'center' || align === 'CENTER') {
          textData.textAlign = 'center';
        } else if (align === 2 || align === 'right' || align === 'RIGHT') {
          textData.textAlign = 'right';
        } else if (align === 3 || align === 'justify' || align === 'JUSTIFY') {
          textData.textAlign = 'justify';
        }
      }
    }
  }
}

// Helper: Extract letter spacing from engineData
function extractLetterSpacingFromEngineData(engineData: any, textData: Partial<LayerJSON>): void {
  if (engineData?.Editor?.Text?.StyleSheet?.StyleSheetData) {
    const styleData = engineData.Editor.Text.StyleSheet.StyleSheetData;
    if (styleData.Tracking || styleData.tracking) {
      textData.letterSpacing =
        textData.letterSpacing ?? (styleData.Tracking || styleData.tracking);
    }
  }
  
  if (textData.letterSpacing === undefined && engineData?.Editor?.Text?.StyleRun) {
    const styleRun = engineData.Editor.Text.StyleRun;
    if (styleRun && styleRun.length > 0) {
      const run = styleRun[0];
      if (run.StyleSheet?.StyleSheetData?.Tracking || run.StyleSheet?.StyleSheetData?.tracking) {
        textData.letterSpacing =
          run.StyleSheet.StyleSheetData.Tracking || run.StyleSheet.StyleSheetData.tracking;
      }
    }
  }
}

// Helper: Process engineData
function processEngineData(textLayerData: any, layerId: string, textData: Partial<LayerJSON>): void {
  try {
    let engineData: any;
    if (typeof textLayerData.engineData === 'string') {
      engineData = JSON.parse(textLayerData.engineData);
    } else {
      engineData = textLayerData.engineData;
    }
    
    extractFontFromEngineData(engineData, textData);
    extractAlignmentFromEngineData(engineData, textData);
    extractLetterSpacingFromEngineData(engineData, textData);
  } catch (error) {
    console.warn(`Warning: Could not parse text engineData for layer ${layerId}:`, error);
  }
}

// Helper: Process text style
function processTextStyle(style: any, textData: Partial<LayerJSON>): void {
  extractFontFromStyle(style, textData);

  if (typeof style.fontSize === 'number') {
    textData.fontSize = style.fontSize;
  }

  if (typeof style.tracking === 'number') {
    textData.letterSpacing = style.tracking;
  } else if (typeof style.kerning === 'number') {
    textData.letterSpacing = style.kerning;
  }

  if (typeof style.leading === 'number') {
    textData.style ??= {};
    textData.style.leading = style.leading;
  }

  if (typeof style.autoLeading === 'boolean') {
    textData.style ??= {};
    textData.style.autoLeading = style.autoLeading;
  }

  if (style.fillColor) {
    extractColorFromFill(style.fillColor, textData);
  }
}

// Helper: Process paragraph style
function processParagraphStyle(paragraphStyle: any, textData: Partial<LayerJSON>): void {
  const just = paragraphStyle.justification;
  const validAlignments = ['left', 'center', 'right', 'justify'];
  if (validAlignments.includes(just)) {
    textData.textAlign = just;
  }
}

function mapTextLayer(layer: Layer, layerId: string): Partial<LayerJSON> {
  const textData: Partial<LayerJSON> = {};
  
  if (!layer.text) {
    return textData;
  }
  
  const textLayerData = layer.text as any;
  textData.text = textLayerData.value || textLayerData.text || '';

  if (textLayerData.style) {
    processTextStyle(textLayerData.style, textData);
  }

  if (textLayerData.paragraphStyle) {
    processParagraphStyle(textLayerData.paragraphStyle, textData);
  }
  
  if (textLayerData.engineData) {
    processEngineData(textLayerData, layerId, textData);
  }
  
  return textData;
}

function mapShapeLayer(layer: Layer): Partial<LayerJSON> {
  const shapeData: Partial<LayerJSON> = {};
  
  if (layer.name?.toLowerCase().includes('rectangle')) {
    shapeData.shapeType = 'rectangle';
  } else if (layer.name?.toLowerCase().includes('ellipse') || layer.name?.toLowerCase().includes('circle')) {
    shapeData.shapeType = 'ellipse';
  } else {
    shapeData.shapeType = 'path';
  }
  
  return shapeData;
}

// Helper: Extract transform from layer properties
function extractLayerTransform(layer: Layer): number[] | undefined {
  const layerAny = layer as any;
  
  if (Array.isArray(layerAny.transform)) {
    return layerAny.transform;
  }
  if (Array.isArray(layerAny.matrix)) {
    return layerAny.matrix;
  }
  if (Array.isArray(layerAny.transformMatrix)) {
    return layerAny.transformMatrix;
  }
  if (Array.isArray(layerAny.transformation)) {
    return layerAny.transformation;
  }
  if (layerAny.additionalLayerInfo) {
    const additionalInfo = layerAny.additionalLayerInfo;
    if (Array.isArray(additionalInfo.transform)) {
      return additionalInfo.transform;
    }
    if (Array.isArray(additionalInfo.matrix)) {
      return additionalInfo.matrix;
    }
  }
  
  return undefined;
}

// Helper: Extract transform from text layer engine data
function extractTextLayerTransform(textLayerData: any, layerId: string): number[] | undefined {
  if (Array.isArray(textLayerData.transform)) {
    return textLayerData.transform;
  }
  if (Array.isArray(textLayerData.matrix)) {
    return textLayerData.matrix;
  }
  if (Array.isArray(textLayerData.style?.transform)) {
    return textLayerData.style.transform;
  }
  
  if (textLayerData.engineData) {
    try {
      let engineData: any;
      if (typeof textLayerData.engineData === 'string') {
        engineData = JSON.parse(textLayerData.engineData);
      } else {
        engineData = textLayerData.engineData;
      }
      if (Array.isArray(engineData?.Editor?.Text?.Transform)) {
        return engineData.Editor.Text.Transform;
      }
      if (Array.isArray(engineData?.Transform)) {
        return engineData.Transform;
      }
    } catch (e) {
      console.debug(`Could not parse engineData for layer ${layerId}:`, e);
    }
  }
  
  return undefined;
}

// Helper: Process group layer
async function processGroupLayer(
  layer: Layer,
  baseLayer: LayerJSON,
  imagesToUpload: ImageToUpload[],
  layerPath: string
): Promise<void> {
  baseLayer.type = 'group';
  baseLayer.children = [];
  
  if ('children' in layer && Array.isArray(layer.children)) {
    for (const child of layer.children) {
      const childJSON = await mapLayerToJSON(child, imagesToUpload, layerPath);
      if (childJSON) {
        baseLayer.children.push(childJSON);
      }
    }
  }
}

// Helper: Process text layer
async function processTextLayer(
  layer: Layer,
  baseLayer: LayerJSON,
  layerId: string
): Promise<void> {
  baseLayer.type = 'text';
  const textData = mapTextLayer(layer, layerId);
  Object.assign(baseLayer, textData);
  
  const left = layer.left || 0;
  const top = layer.top || 0;
  const right = layer.right || 0;
  const bottom = layer.bottom || 0;
  baseLayer.boxBounds = [left, top, right - left, bottom - top];
  
  const textLayerData = layer.text as any;
  
  // Store raw layer data for line spacing calculation
  baseLayer.rawLayerData = {
    text: textLayerData
  };
  
  if (textLayerData.bounds) {
    baseLayer.bounds = {
      left: textLayerData.bounds.left,
      top: textLayerData.bounds.top
    };
  }
  if (textLayerData.style) {
    // Initialize style object
    baseLayer.style = {
      fontSize: textLayerData.style.fontSize,
      leading: textLayerData.style.leading,
      autoLeading: textLayerData.style.autoLeading
    };
    
    // Copy font object from PSD text style to layer.style.font
    // PSD font object structure: { name: "Poppins-Regular", script: 0, type: 0, synthetic: 0 }
    if (textLayerData.style.font) {
      const styleWithFont = baseLayer.style as any;
      styleWithFont.font = {
        name: textLayerData.style.font.name || undefined,
        family: textLayerData.style.font.family || undefined // May not exist in PSD
      };
      console.log(`✅ Copied font object for layer ${baseLayer.name}:`, styleWithFont.font);
    } else {
      console.warn(`⚠️ No font object found in textLayerData.style for layer ${baseLayer.name}`);
      console.log('textLayerData.style:', textLayerData.style);
    }
  } else {
    console.warn(`⚠️ No style object found in textLayerData for layer ${baseLayer.name}`);
  }
}

// Helper: Process image layer
async function processImageLayer(
  layer: Layer,
  baseLayer: LayerJSON,
  layerId: string,
  imagesToUpload: ImageToUpload[]
): Promise<void> {
  baseLayer.type = 'image';
  const imageBuffer = await extractImageBuffer(layer, layerId);
  if (imageBuffer) {
    const filename = `${layerId}.png`;
    imagesToUpload.push({
      layerId,
      buffer: imageBuffer,
      filename
    });
    baseLayer.imagePath = layerId;
  }
}

// Helper: Process shape or default layer
function processShapeOrDefaultLayer(
  layer: Layer,
  baseLayer: LayerJSON
): void {
  const layerName = layer.name || '';
  const isShape = layerName.toLowerCase().includes('shape') || 
                  layerName.toLowerCase().includes('vector');
  
  if (isShape) {
    baseLayer.type = 'shape';
    const shapeData = mapShapeLayer(layer);
    Object.assign(baseLayer, shapeData);
  } else {
    baseLayer.type = 'image';
  }
}

export async function mapLayerToJSON(
  layer: Layer,
  imagesToUpload: ImageToUpload[],
  parentPath: string = ''
): Promise<LayerJSON | null> {
  if (layer.hidden) {
    return null;
  }
  
  const layerId = generateLayerId();
  const layerPath = parentPath ? `${parentPath}/${layer.name || layerId}` : (layer.name || layerId);
  
  let transform = extractLayerTransform(layer);
  
  if (!transform && layer.text) {
    transform = extractTextLayerTransform(layer.text as any, layerId);
  }
  
  const isVisible = layer.hidden === false || layer.hidden === undefined;
  const layerOpacity = layer.opacity;
  const hasOpacity = layerOpacity !== undefined;
  const opacity = hasOpacity ? layerOpacity / 255 : 1;
  const rawOpacity = hasOpacity ? layerOpacity : 255;
  
  const baseLayer: LayerJSON = {
    id: layerId,
    name: layer.name || 'Unnamed Layer',
    type: 'image',
    visible: isVisible,
    opacity: opacity,
    rawOpacity: rawOpacity,
    blendMode: getBlendMode(layer),
    boundingBox: getBoundingBox(layer),
    transform: transform
  };
  
  if (isGroupLayer(layer)) {
    await processGroupLayer(layer, baseLayer, imagesToUpload, layerPath);
  } else if (isTextLayer(layer)) {
    await processTextLayer(layer, baseLayer, layerId);
  } else if (isImageLayer(layer)) {
    await processImageLayer(layer, baseLayer, layerId, imagesToUpload);
  } else {
    processShapeOrDefaultLayer(layer, baseLayer);
  }
  
  return baseLayer;
}

function replaceImagePathsWithS3Urls(json: PSDJSON, layerIdToUrlMap: Map<string, string>): void {
  function processLayer(layer: LayerJSON): void {
    if (layer.imagePath && layerIdToUrlMap.has(layer.imagePath)) {
      layer.imagePath = layerIdToUrlMap.get(layer.imagePath)!;
    }
    if (layer.children) {
      layer.children.forEach(processLayer);
    }
  }
  
  json.layers.forEach(processLayer);
}

export async function mapPSDToJSON(psd: Psd): Promise<PSDJSON> {
  layerIdCounter = 0;
  
  const canvas: CanvasJSON = {
    width: psd.width || 0,
    height: psd.height || 0
  };
  
  const layers: LayerJSON[] = [];
  const imagesToUpload: ImageToUpload[] = [];
  
  if (psd.children) {
    for (const layer of psd.children) {
      const layerJSON = await mapLayerToJSON(layer, imagesToUpload);
      if (layerJSON) {
        layers.push(layerJSON);
      }
    }
  }
  
  if (imagesToUpload.length > 0) {
    console.log(`Uploading ${imagesToUpload.length} images to S3...`);
    const layerIdToUrlMap = await uploadImagesToS3(imagesToUpload);
    replaceImagePathsWithS3Urls({ canvas, layers }, layerIdToUrlMap);
  }
  
  return {
    canvas,
    layers
  };
}
